import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchData,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
} from "../../../mainApp/features/DataSlice/dataSlice";
import LoadingPage from "../TechnicalTabs/LoadingPage";
import ErrorPage from "../TechnicalTabs/ErrorPage";
import { handleUnauthorizedAccess } from "../Utility/handleUnauthorizedAccess";

const ProfilePageWrapper = styled.div`
  margin-top: calc(104px); /* Odstęp od górnej nawigacji */
  padding: 32px;
  width: calc(100% - 70px); /* Odstęp od lewej nawigacji */
  box-sizing: border-box;
  min-height: calc(100vh - 104px);
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletMax}px) {
    width: 100%;
    padding: 24px 16px;
  }
`;

const ProfileContainer = styled.div`
  background: ${({ theme }) => theme.color.spotifyDarkGrey};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.spotifyLightGrey};
  margin-bottom: 24px;
`;

const ProfileImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-right: 16px;
  object-fit: cover;
`;

const ProfileName = styled.h2`
  color: ${({ theme }) => theme.color.spotifyWhite};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.color.spotifyGrey};
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: ${({ theme }) => theme.color.spotifyLightGrey};
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.color.spotifyWhite};
  font-size: 14px;
  outline: none;
  transition: background 0.2s ease;

  &:focus {
    background: ${({ theme }) => theme.color.spotifyGrey};
  }
`;

const EmailText = styled.p`
  color: ${({ theme }) => theme.color.spotifyWhite};
  font-size: 14px;
  margin: 0 0 20px 0;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 10px 16px;
  width: 16vh;
  text-align: center;
  background: ${({ theme }) => theme.color.spotifyDarkGreen};
  color: ${({ theme }) => theme.color.brightWhite};
  font-size: 14px;
  font-weight: 400;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.color.spotifyBrightGreen};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.p`
  color: #ff4d4d;
  font-size: 12px;
  margin: 8px 0 0 0;
`;

const SuccessMessage = styled.p`
  color: ${({ theme }) => theme.color.spotifyBrightGreen};
  font-size: 12px;
  margin: 8px 0 0 0;
`;

const SaveButton = styled.button`
  padding: 10px 16px;
  background: ${({ theme }) => theme.color.spotifyDarkGreen};
  color: ${({ theme }) => theme.color.brightWhite};
  width: 16vh;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.color.spotifyBrightGreen};
  }
`;

const ProfilePage = () => {
  const [userDetails, setUserDetails] = useState({
    email: "",
    firstName: "",
    lastName: "",
    photo: "",
  });
  const [newPhoto, setNewPhoto] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const storage = getStorage();

  // Funkcja opóźnienia
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const docRef = doc(db, "Users", currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserDetails({
              email: currentUser.email,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              photo: data.photo || "",
            });
          } else {
            console.warn("Dokument użytkownika nie istnieje.");
            setUserDetails({
              email: currentUser.email,
              firstName: "",
              lastName: "",
              photo: "",
            });
          }
          await delay(600); // Dodatkowe opóźnienie jako zabezpieczenie
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          setUserDetails({
            email: currentUser.email,
            firstName: "",
            lastName: "",
            photo: "",
          });
          await delay(600); // Opóźnienie w przypadku błędu
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2.5 * 1024 * 1024; // 2.5 MB w bajtach
      if (file.size > maxSize) {
        setError("Zdjęcie jest za duże. Maksymalny rozmiar to 2.5 MB.");
        setNewPhoto(null);
      } else {
        setNewPhoto(file);
        setError("");
        setSuccess("");
      }
    }
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      let photoURL = userDetails.photo;
      if (newPhoto) {
        const storageRef = ref(storage, `profile_photos/${user.uid}/${newPhoto.name}`);
        await uploadBytes(storageRef, newPhoto);
        photoURL = await getDownloadURL(storageRef);
      }

      const userRef = doc(db, "Users", user.uid);
      await updateDoc(userRef, {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        photo: photoURL,
      });

      setUserDetails((prev) => ({ ...prev, photo: photoURL }));
      setNewPhoto(null);
      setSuccess("Profil zaktualizowany pomyślnie!");
      setError("");
    } catch (err) {
      setError("Wystąpił błąd podczas zapisywania zmian.");
      console.error(err);
    }
  };

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ProfilePageWrapper>
            <ProfileContainer>
              <ProfileHeader>
                <ProfileImage
                  src={
                    newPhoto
                      ? URL.createObjectURL(newPhoto)
                      : userDetails.photo || require("../Assets/jakub.png")
                  }
                  alt="Profile"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = require("../Assets/jakub.png");
                  }}
                />
                <ProfileName>
                  {userDetails.firstName} {userDetails.lastName}
                </ProfileName>
              </ProfileHeader>

              <FormField>
                <Label>Adres e-mail</Label>
                <EmailText>{userDetails.email}</EmailText>
              </FormField>

              <FormField>
                <Label>Imię</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={userDetails.firstName}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField>
                <Label>Nazwisko</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={userDetails.lastName}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField>
                <Label>Zdjęcie profilowe</Label>
                <FileInputLabel>
                  Wybierz zdjęcie
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </FileInputLabel>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
              </FormField>

              <SaveButton onClick={handleSave}>Zapisz zmiany</SaveButton>
            </ProfileContainer>
          </ProfilePageWrapper>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default ProfilePage;